import React, { type CSSProperties, useMemo } from "react";
import { CircularProgress, Icon, Typography } from "@mui/material";

import {
  getFieldLabel,
  removeNotToShowFields,
  REQUEST_FIELD_LABELS,
} from "@/features/brand-request/utils/formConfigs";
import { containsUrls, getImage, urlRegex } from "@/helpers/utils";
import RpSvgIcon from "@/components/icons/RpSvgIcon";

type RequestDetailPrimitive = string | number | boolean | null | undefined;

type RequestDetailObject = {
  start_date?: string;
  startDate?: string;
  from?: string;
  end_date?: string;
  endDate?: string;
  to?: string;
  file_url?: string;
  file_name?: string;
  location_name?: string;
  channel_name?: string;
  campaign_goal?: string;
  group_name?: string;
  [key: string]: RequestDetailValue;
};

export type RequestDetailValue =
  | RequestDetailPrimitive
  | RequestDetailObject
  | RequestDetailObject[]
  | RequestDetailPrimitive[];

export type RequestDetailData = {
  date_range_start_date?: string;
  date_range_end_date?: string;
  flight_date?: RequestDetailObject[];
  [key: string]: RequestDetailValue;
};

type DisplayField = {
  key: string;
  label: string;
  value: RequestDetailValue;
};

type SingleRequestDetailProps = {
  data?: RequestDetailData | null;
  isLoading?: boolean;
  requestType?: string;
  requestId?: string;
};

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "10px",
  },
  header: {
    marginBottom: "20px",
  },
  requestType: {
    fontWeight: "bold",
    marginBottom: "16px",
  },
  fields: {
    display: "flex",
    flexDirection: "column",
  },
  field: {
    marginBottom: "22px",
  },
  fieldLabel: {
    marginBottom: "8px",
  },
  fieldInput: {
    padding: "8px",
    border: "1px solid var(--neutral-700)",
    borderRadius: "var(--radius-container)",
    backgroundColor: "var(--neutral-200)",
    wordWrap: "break-word",
  },
  urlField: {
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal",
  },
};

function RequestDetails({
  data,
  isLoading,
  requestType = "",
  requestId = "",
}: SingleRequestDetailProps) {
  const formattedData = useMemo(() => {
    if (!data) return null;
    const result = Object.keys(data)
      .filter((k) => {
        const value = data[k];
        return Array.isArray(value) ? value.length : value;
      })
      .filter((k) => !removeNotToShowFields.includes(k))
      .reduce<RequestDetailData>((t, k) => ({ ...t, [k]: data[k] }), {});

    // Combine single date range into one field
    if (data.date_range_start_date && data.date_range_end_date) {
      result.date_range = `${data.date_range_start_date} To ${data.date_range_end_date}`;
      delete result.date_range_start_date;
      delete result.date_range_end_date;
    }
    // Handle multiple flight_dates
    if (Array.isArray(data.flight_date) && data.flight_date.length > 0) {
      const validFlightDates = data.flight_date.filter((fd) => {
        // Check for different possible property names
        const startDate = fd?.start_date || fd?.startDate || fd?.from;
        const endDate = fd?.end_date || fd?.endDate || fd?.to;
        return (
          startDate &&
          endDate &&
          startDate !== "00/00/0000" &&
          endDate !== "00/00/0000"
        );
      });

      validFlightDates.forEach((fd, index) => {
        // Get the actual date values using different possible property names
        const startDate = fd?.start_date || fd?.startDate || fd?.from || "";
        const endDate = fd?.end_date || fd?.endDate || fd?.to || "";
        result[`flight_date_${index + 1}`] = `${startDate} To ${endDate}`;
      });
    }

    return result;
  }, [data]);

  const renderData = useMemo(() => {
    if (!formattedData) return null;

    // Create an ordered list of fields based on the request type's field order
    const orderedFields = Object.keys(formattedData).map((key) => ({
      key,
      label: getFieldLabel(key, requestType),
      value: formattedData[key],
    }));

    // Sort fields based on the order defined in REQUEST_FIELD_LABELS
    const sortedFields = orderedFields.sort((a, b) => {
      const requestTypeLabels = REQUEST_FIELD_LABELS[requestType] || {};

      // Get the order of fields in the request type labels
      const aOrder = Object.keys(requestTypeLabels).indexOf(a.key);
      const bOrder = Object.keys(requestTypeLabels).indexOf(b.key);
      // If both fields are in request type labels, sort by their order
      if (aOrder !== -1 && bOrder !== -1) {
        return aOrder - bOrder;
      }
      // If only one field is in request type labels, it should come first
      if (aOrder !== -1) return -1;
      if (bOrder !== -1) return 1;

      // If neither field is in any labels, maintain original order
      return 0;
    });

    // Separate URL fields and other fields
    const urlFields = sortedFields.filter((field) => containsUrls(field.value));
    const otherFields = sortedFields.filter(
      (field) => !containsUrls(field.value)
    );

    const renderField = (field: DisplayField): React.ReactNode => {
      const { value } = field;

      // Handle arrays (locations, channels, etc.)
      if (Array.isArray(value)) {
        // Handle array of files/URLs
        if (field.key === "ad_copy_files" || containsUrls(value)) {
          return (
            <div className="d-flex flex-column gap-2">
              {value.map((item, index) => {
                const fileItem = item as RequestDetailObject | string;
                const fileName =
                  typeof fileItem === "string"
                    ? fileItem.split("/").pop()
                    : fileItem.file_name || fileItem.file_url?.split("/").pop();
                return (
                  <div key={index} style={styles.urlField}>
                    <Icon
                      component={RpSvgIcon}
                      name="file_icon"
                      color="primary"
                      size="large"
                      style={{ fontSize: "4.5rem" }}
                      className="single-request-detail-file-link-icon mr-3 mt-2"
                    />

                    <a
                      href={getImage(
                        typeof fileItem === "string"
                          ? undefined
                          : fileItem.file_url,
                        "ASSET_DOWNLOAD_URL"
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="single-request-detail-file-link"
                    >
                      {fileName}
                    </a>
                  </div>
                );
              })}
            </div>
          );
        }

        // Handle locations and channels
        if (
          field.key === "locations" ||
          field.key === "channel" ||
          field.key === "ad_copy_files" ||
          field.key === "campaign_goal" ||
          field.key === "groups"
        ) {
          return value
            .map((item) => {
              const itemObject = item as RequestDetailObject;
              return (
                itemObject.location_name ||
                itemObject.channel_name ||
                itemObject.campaign_goal ||
                itemObject.group_name
              );
            })
            .join(", ");
        }

        return value.join(", "); // For other arrays
      }

      // Handle single URL
      if (typeof value === "string" && urlRegex && urlRegex.test(value)) {
        return (
          <div style={styles.urlField}>
            <Icon
              component={RpSvgIcon}
              name="file_icon"
              color="primary"
              size="large"
              style={{ fontSize: "4.5rem" }}
              className="single-request-detail-file-link-icon mr-3 mt-2"
            />
            <a
              href={getImage(value, "ASSET_DOWNLOAD_URL")}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="single-request-detail-file-link"
            >
              {value && value.split("/").pop()}
            </a>
          </div>
        );
      }

      return (value || "N/A") as React.ReactNode;
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.field}>
            <Typography
              className="text-capitalize fs-12"
              variant="h6"
              style={styles.fieldLabel}
            >
              Request Id
            </Typography>
            <div style={styles.fieldInput}>
              <Typography>{requestId ? requestId : "N/A"}</Typography>
            </div>
          </div>
          <div style={styles.field}>
            <Typography
              className="text-capitalize fs-12"
              variant="h6"
              style={styles.fieldLabel}
            >
              What can we help you with?
            </Typography>
            <div style={styles.fieldInput}>
              <Typography>{requestType ? requestType : "N/A"}</Typography>
            </div>
          </div>
        </div>
        <div style={styles.fields}>
          {otherFields.length > 0
            ? otherFields.map((field) => (
                <div key={field.key} style={styles.field}>
                  <Typography
                    className="text-capitalize fs-12"
                    variant="h6"
                    style={styles.fieldLabel}
                  >
                    {field.label}
                  </Typography>
                  <div style={styles.fieldInput}>
                    <Typography>{renderField(field)}</Typography>
                  </div>
                </div>
              ))
            : otherFields.length > 0 &&
              urlFields.length > 0 && (
                <Typography>No fields available to display.</Typography>
              )}
          {urlFields.length > 0 &&
            urlFields.map((urlField) => (
              <div style={styles.field}>
                <Typography
                  className="text-capitalize fs-12"
                  variant="h6"
                  style={styles.fieldLabel}
                >
                  {urlField.label}
                </Typography>
                <div className="mt-1">
                  <Typography>{renderField(urlField)}</Typography>
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  }, [formattedData, requestId, requestType]);

  if (isLoading)
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <CircularProgress color="primary" size={20} />
      </div>
    );

  return <div>{renderData}</div>;
}

export default RequestDetails;
